/* tslint:disable:import-spacing component-class-suffix no-inferrable-types variable-name
   typedef-whitespace component-selector no-trailing-whitespace */

import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output
}                                  from '@angular/core';

import {DecisionPoint}             from '@/models/scenario/decision.point';
import {
  TrainClass,
  TrainDirection
}                                  from '@/models/scenario/train.class';
import {Scenario}                  from '@/models/scenario/scenario';
import {Logger}                    from '@/util/logger';
import {TrainClassesEditor}        from '@/helpers/train.classes.editor';
import {TrainClassesRenderer}      from '@/helpers/train.classes.renderer';
import {NumberEditor}              from '@/helpers/number.editor';
import {PositiveNumberEditor}      from '@/helpers/positive.number.editor';
import {WholeNumberEditor}         from '@/helpers/whole.number.editor';
import {WholeNumberRenderer}       from '@/helpers/whole.number.renderer';
import {WholePositiveNumberEditor} from '@/helpers/whole.positive.number.editor';
import {PeriodEditor}              from '@/helpers/period.editor';
import {DecisionPointEditor}       from '@/helpers/decision.point.editor';
import {DecisionPointRenderer}     from '@/helpers/decision.point.renderer';
import {Train}                     from '@/models/scenario/train';
import {PeriodRenderer}            from "@/helpers/period.renderer";
import {Period}                    from "@/util/period";
import {TrackIngress}              from '@/helpers/track.ingress';
import {TrackRenderer}             from '@/helpers/track.renderer';
import {TrackEditor}               from '@/helpers/track.editor';
import {Track}                     from "@/models/scenario/track";
import {StatusReporter}            from '@/util/status.reporter';
import {UnDoRedo}                  from '@/util/un.do.redo';
import {SetTrainFieldCommand}      from '@/screens/ide/scenariobrowser/set.train.field.command';
import {ICommand}                  from '@/util/i.command';

import 'ag-grid-community';

import {
  CellClassParams,
  CellPosition,
  CellValueChangedEvent,
  Column,
  CsvExportParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  ProcessCellForExportParams,
  RowClassParams,
  TabToNextCellParams,
  ExcelExportParams,
  GetContextMenuItemsParams
} from 'ag-grid-community';

import {basename} from "@/util/util";
import {ConnectionViewerMetadata} from "@/screens/ide/connection.viewer.metadata";

@Component({
  selector: 'train-table',
  template: `
    <div id="train-table-container_{{uuid}}" class="train-table-container" style="">
      <ag-grid-angular id="ag-grid-train-table_{{uuid}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-train-table"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [rowClassRules]="rowClassRules"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        [tabToNextCell]="tabToNextCell"

        [components]="components"

        (gridReady)="onGridReady($event)"

      >
      </ag-grid-angular>
    </div>`
})

/**
 *
 * @class TrainTable
 *
 * @brief this table shows the trains
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      October 2019
 *
 */
export class TrainTable implements AfterViewInit {

  @Input() uuid: number                 = 0;

  /** @var _scenario the scenario model */
  private _scenario: Scenario           = null;

  /** the API for the widget */
  private          api: GridApi         = null;

  private isFullScreen: boolean         = false;

  /** where we are sending our status messages */
  public statusReporter: StatusReporter = null;

  /** the set of all current errors for all trains */
  public _trainErrors: string[]         = [];

  /** the set of all warnings */
  public _TrainWarnings: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

  public context          = {

    trainTable: this

  };

  public components = {
    trainClassEditor:          TrainClassesEditor,
    numberEditor:              NumberEditor,
    positiveNumberEditor:      PositiveNumberEditor,
    wholeNumberEditor:         WholeNumberEditor,
    wholePositiveNumberEditor: WholePositiveNumberEditor,
    periodEditor:              PeriodEditor,
    dpEditor:                  DecisionPointEditor,
    trackEditor:               TrackEditor
  };

  public rowClassRules: any = {
    'scenario-trains-warning': (params: RowClassParams): boolean => {

      if (typeof params.node.data == 'undefined') {

        return false;
      }

      const train: Train = this.trainOfId(params.node.data.id);

      if (!train) {

        return false;
      }

      let trainName: string = train.quickName();

      /* force validity check for any critical rows BEFORE ag-grid does its rendering */

      params.context.trainTable.fieldValid('days', params.node.data, params.node.data.days, params.node);

      if (!this._TrainWarnings.has(trainName)) {

        /* no warnings registered for that train yet */

        return false;
      }

      /* even if the train has warnings, it might be on the 'days' field. */

      return (this._TrainWarnings.get(trainName).get('days') ?? []).length != 0;
    }
  };

  public columnDefs: any[] = [

    {
      headerName: 'Train',
      children: [
        {headerName: 'ROW',         field: 'row',      hide: true},
        {headerName: 'Type',        field: 'type',     hide: true, rowGroup: true},
        {headerName: 'Train ID',    field: 'id',       columnGroupShow: 'open', width: 50},
        {
          headerName: 'Name',
          field: 'name',
          editable: true,
          width: 150,
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value, params.node);
            }
          }
        }
      ]
    },

    {
      headerName: 'Consist',
      children: [
        {
          headerName: 'Speed Class',
          field: 'trainclass',
          editable: true,
          cellRenderer: TrainClassesRenderer,
          cellEditor: 'trainClassEditor',
          equals:       (a: TrainClass, b: TrainClass): boolean =>  {

            if (!( a instanceof TrainClass ) || !( b instanceof TrainClass )) {

              return a === b;
            }

            return a._id === b._id;
          },

          cellEditorParams: {
            trainClasses: () => {
              return this._scenario.trainClasses();
            }
          },
          width: 100,
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);

            const        row: IRowNode  = e.node;

            this.api.redrawRows({rowNodes: [row]});
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },

        {
          headerName: 'Length',
          field: 'length',
          columnGroupShow: 'open',
          width: 100,
          editable: true,
          cellEditorParams: {
            cellRenderer: WholeNumberRenderer,
          },
          cellEditor:   'wholePositiveNumberEditor',
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Tonnage',
          field: 'tonnage',
          columnGroupShow: 'open',
          width: 100,
          editable: true,
          cellEditorParams: {
            cellRenderer: WholeNumberRenderer,
          },
          cellEditor:   'wholePositiveNumberEditor',
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Priority',
          field: 'priority',
          columnGroupShow: 'open',
          width: 100,
          editable: true,
          cellEditorParams: {
            cellRenderer: WholeNumberRenderer,
          },
          cellEditor:   'wholePositiveNumberEditor',
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Train Class ID',
          field: 'tcid',
          columnGroupShow: 'open',
          width: 100,
          editable: true,
          cellEditorParams: {
            cellRenderer: WholeNumberRenderer,
          },
          cellEditor:   'wholePositiveNumberEditor',
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        }
      ]
    },

    {
      headerName: 'Schedule',
      children: [

        {
          headerName: 'Start',
          field: 'srcdp',
          cellRenderer: DecisionPointRenderer,
          cellEditorParams: {

            dpList: (): DecisionPoint[] => {

              return this._scenario.milePoints();
            },
            train: (id: number): Train => {

              return this.trainOfId(id);
            },
            start: true
          },
          cellEditor:   'dpEditor',
          equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

            if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

              return a === b;
            }

            return (a._id === b._id) && (a._name === b._name);
          },

          onCellValueChanged: (e: CellValueChangedEvent) => {

            const      newDP: DecisionPoint = e.newValue;
            const         tt: TrainTable    = e.context.trainTable;
            const        row: IRowNode      = e.node;
            const oldIngress: TrackIngress  = e.data['src'];

            /* track the history of source decision point change */

            const cmd: ICommand = new SetTrainFieldCommand('srcdp', oldIngress, newDP, this, row);

            this.historyManager().insert(cmd);

            /* update the source track column... */

            tt.setDefaultTrack('src', newDP, row);

          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid('src', params.data, params.data.src);
            }
          },
          editable: true,
          width: 150
        },
        {
          headerName: 'Start Track',
          field: 'src',
          cellRenderer: TrackRenderer,
          cellRendererParams: {

            train: (id: number): Train => {

              return this.trainOfId(id);
            }
          },
          cellEditorParams: {

            train: (id: number): Train => {

              return this.trainOfId(id);
            }

          },
          cellEditor:   'trackEditor',

          equals:       (a: TrackIngress, b: TrackIngress): boolean =>  {

            if ((typeof a === 'undefined') && (typeof b === 'undefined')) {

              /* header row */

              return true;
            }

            if ((a === null) || (b === null)) {

              return a === b;
            }

            if (!( a instanceof TrackIngress ) || !( b instanceof TrackIngress )) {

              if ((a.track === null) || (b.track === null)) {

                return a.track === b.track;
              }

              return a === b;
            }

            if ((a.track === null) || (b.track === null)) {

              return a.track === b.track;
            }

            return a.track._id === b.track._id;
          },

          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged('src', e.oldValue, e.newValue, e.node);

            const        row: IRowNode  = e.node;

            this.api.redrawRows({rowNodes: [row]});
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid('src', params.data, params.data.src);
            }
          },
          editable: true,
          width: 150,
          columnGroupShow: 'open'
        },

        {
          headerName: 'End',
          field: 'dstdp',
          cellRenderer: DecisionPointRenderer,
          cellEditorParams: {

            dpList: (): DecisionPoint[] => {

              return this._scenario.milePoints();
            },

            train: (id: number): Train => {

              return this.trainOfId(id);
            },
            start: false
          },
          cellEditor:   'dpEditor',
          equals:       (a: DecisionPoint, b: DecisionPoint): boolean =>  {

            if (!( a instanceof DecisionPoint ) || !( b instanceof DecisionPoint )) {

              return a === b;
            }

            return (a._id === b._id) && (a._name === b._name);
          },

          onCellValueChanged: (e: CellValueChangedEvent) => {

            const      newDP: DecisionPoint = e.newValue;
            const         tt: TrainTable    = e.context.trainTable;
            const        row: IRowNode      = e.node;
            const oldIngress: TrackIngress  = e.data['dst'];

            /* track the history of destination decision point change */

            const cmd: ICommand = new SetTrainFieldCommand('dstdp', oldIngress, newDP, this, row);

            this.historyManager().insert(cmd);

            /* update the source track column... */

            tt.setDefaultTrack('dst', newDP, row);

          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid('dst', params.data, params.data.dst);
            }
          },
          editable: true,
          width: 150
        },
        {
          headerName: 'End Track',
          field: 'dst',
          cellRenderer: TrackRenderer,
          cellRendererParams: {

            train: (id: number): Train => {

              return this.trainOfId(id);
            }
          },
          cellEditorParams: {

            train: (id: number): Train => {

              return this.trainOfId(id);
            }
          },
          cellEditor:   'trackEditor',

          equals:       (a: TrackIngress, b: TrackIngress): boolean =>  {

            if ((typeof a === 'undefined') && (typeof b === 'undefined')) {

              /* header row */

              return true;
            }

            if ((a === null) || (b === null)) {

              return a === b;
            }

            if (!( a instanceof TrackIngress ) || !( b instanceof TrackIngress )) {

              if ((a.track === null) || (b.track === null)) {

                return a.track === b.track;
              }

              return a === b;
            }

            if ((a.track === null) || (b.track === null)) {

              return a.track === b.track;
            }

            return a.track._id === b.track._id;
          },

          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged('dst', e.oldValue, e.newValue, e.node);

            const        row: IRowNode  = e.node;

            this.api.redrawRows({rowNodes: [row]});
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid('dst', params.data, params.data.dst);
            }
          },
          editable: true,
          width: 150,
          columnGroupShow: 'open'
        },

        {
          headerName: 'Depart',
          field: 'departure',
          columnGroupShow: 'open',
          width: 100,
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          cellRenderer:  PeriodRenderer,
          cellEditor:   'periodEditor',
          equals:       (a: Period, b: Period): boolean =>  {

            if (!( a instanceof Period ) || !( b instanceof Period )) {

              return a === b;
            }

            return a.asString() === b.asString();
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        }

      ]
    },

    {
      headerName: 'Frequency',
      children: [
        {
          headerName: 'Day of Week',
          field: 'days',
          width: 100,
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
            e.context.trainTable.api.refreshCells();
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value, params.node);
            }
          },
        },
        {
          headerName: 'Daily',
          field: 'daily',
          columnGroupShow: 'open',
          width: 100,
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Weekly',
          field: 'weekly',
          columnGroupShow: 'open',
          width: 100,
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        }
      ]
    },

    {
      headerName: 'Statistics',
      children: [
        {
          headerName: 'Max Delay',
          field: 'overtake',
          width: 100,
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Seed',
          field: 'seed',
          width: 100,
          columnGroupShow: 'open',
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: '% Early',
          field: 'percentearly',
          width: 100,
          columnGroupShow: 'open',
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: '\u03C3 Early',
          field: 'early',
          width: 100,
          columnGroupShow: 'open',
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: '\u03C3 Late',
          field: 'late',
          width: 100,
          columnGroupShow: 'open',
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        },
        {
          headerName: 'Distribution',
          field: 'distribution',
          width: 200,
          columnGroupShow: 'open',
          editable: (params: any) => {

            return params.context.trainTable.editableField(params.colDef.field, params.data);
          },
          onCellValueChanged: (e: CellValueChangedEvent) => {

            e.context.trainTable.fieldChanged(e.colDef.field, e.oldValue, e.newValue, e.node);
          },
          cellClassRules: {
            'work-input-error': (params: CellClassParams) => {

              if ((typeof params.data === 'undefined') || (params.data === null)) {

                return false;
              }

              return !params.context.trainTable.fieldValid(params.colDef.field, params.data, params.value);
            }
          }
        }
      ]
    },
    {
      headerName: 'Warnings',
      field: 'warnings',
      width: 200,
      cellStyle: {'white-space': 'normal'},
      autoHeight: true,
      valueGetter(params: any): string {

        if ((typeof params.data === 'undefined') || (params.data === null)) {

          return '';
        }

        /* it's a virtual column, show the warnings for this train (of this row) */

        const train: Train = params.context.trainTable.trainOfId(params.data.id);

        if (!train) {

          return '';
        }

        const trainName: string = train.quickName();

        if (!params.context.trainTable._TrainWarnings.has(trainName)) {

          return '';
        }

        /* even if the train has warnings, it might not be on the 'days' field */

        return Array.from(params.context.trainTable._TrainWarnings.get(trainName).get('days') ?? []).join(' \n');
      }
    }
  ];

  public defaultColDef = {
     sortable: true,
       filter: true,
    resizable: true
  };

  public rowData = [];

  @Output() viewPenalty       = new EventEmitter<number>();
  @Output() viewDecisionPoint = new EventEmitter<number>();

  /* general call back for when we reload */
  public onTrainChanged: (train: Train) => void;

  /* allow the parent component to provide to undo manager */
  public historyManager: () => UnDoRedo;

  /** standard constructor */
  constructor() {

    Logger.debug('[train/table] dynamically creating...');

  }

  onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;
  }

  /** update the error line in the loader drop down */
  public status(msg: string =  '') {

    if (this.statusReporter === null) {

      return ;
    }

    this.statusReporter.status(msg);
  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    if (this.statusReporter === null) {

      return ;
    }

    this.statusReporter.success(msg);
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    if (this.statusReporter === null) {

      return ;
    }

    this.statusReporter.error(msg);
  }

  public refreshErrors() {

    this._trainErrors = [];

    if (this._scenario === null) {

      return ;
    }

    const trains: Train[] = this._scenario.scenarioTrains();

    for (const train of trains.values()) {

      if (train._errors.length === 0) {

        continue;
      }

      /* add to our bag of global train errors */

      this._trainErrors =  [].concat(this._trainErrors, train._errors.map(x => `${train.quickName()} ${x}`));
    }

    /*
     * if we have any errors, show at least one of them to the user, so they know they have to
     * fix stuff.
     *
     */

    if (this._trainErrors.length === 0) {

      return ;
    }

    /* show the first known error */

    this.error(this._trainErrors[0]);
  }

  /** helper to focus on the given column / field */
  public focusCell(row: IRowNode, colName: string) {

    let     cols: Column[]  = this.api.getAllDisplayedColumns();
    let col: Column         = cols.find(x => x.getId() === colName);

    /* find the column ... */

    if ((col === null) || (typeof col === 'undefined')) {

      return ;
    }

    /* focus on that cel */

    this.api.ensureColumnVisible(col);
    this.api.setFocusedCell(row.rowIndex, col);
  }

  public tabToNextCell(params: TabToNextCellParams): CellPosition  {

    /* if we're going outside the table...stay on target, stay on target! */

    if ( (typeof params.nextCellPosition === 'undefined') || (params.nextCellPosition === null)) {

      return params.previousCellPosition;
    }

    return params.nextCellPosition;
  }

  /** look up a train class by name */
  public trainClassByName(name: string): TrainClass {

    return this._scenario.trainClasses().find(x => x._name.trim() === name.trim());
  }

  /** look up a train from the scenario by its unique id */
  public trainOfId(id: number): Train {

    return this._scenario.trainById(id);
  }

  public fieldValid(field: string, data: any, newValue: any = null, rowNode: IRowNode|null = null): boolean {

    /*
     * as we do validation, if we find anything that is more of a warning, than an
     * actual error, then append it to the warnings column, rather that erroring out.
     * This lets the user see the warnings are there, but doesn't block them from
     * save/export.
     *
     */

    if ((field == 'srcdp') || (field === 'dstdp')) {

      /* we don't save the decision points in the Train, so we can ignore them. */

      return true;
    }

    if ((field === 'src') && (newValue instanceof TrackIngress)) {

      newValue = (newValue as TrackIngress).track;
    }

    if ((field === 'dst') && (newValue instanceof TrackIngress)) {

      newValue = (newValue as TrackIngress).track;
    }

    /* what train is involved? */

    const train: Train = this.trainOfId(data.id);

    if (train === null) {

      /* no such train? */

      return false;
    }

    if ((field == 'departure') && (newValue instanceof Period)) {

      /** the departure column is a Period editor, but Train uses flat seconds as the simulation time point */

      newValue = newValue.asSeconds();
    }

    if ((field === 'trainclass') && (typeof newValue === 'string')) {

      /*
       * this browser currently implements the train classes picker with the agGrid standard drop down...
       * which is string only, so it's the train class name...not a TrainClass instance (which is what Train
       * needs to work with).
       *
       */

      newValue = this.trainClassByName(newValue);
    }

    /*
     * agGrid might call this before it actually calls its onChange event, which in turn sets the value in the
     * the train object...so depending on timing, the value might not actually be changed yet in the Train object.
     * Because of that, we have to validate by way of "would it be a bad value" rather than "is it already a bad
     * value.
     *
     */

    let trainName: string = train.quickName();

    let errMsg: string = train.checkValue(field, newValue);

    if (!this._TrainWarnings.has(trainName)) {

      /* if no warnings mapping for this train yet, make sure we have an empty set */

      this._TrainWarnings.set(trainName, new  Map<string, string[]>());
    }

    /* if no warnings set for this field of this train yet, then make sure there is at least an empty set. */

    if ((this._TrainWarnings.get(trainName).get(field) ?? []).length == 0) {

      this._TrainWarnings.get(trainName).set(field, []);
    }

    /*
     * whether we have an error or not, we must clear the warnings for this field of this train,
     * because we are re-checking, and the value may have changed (to a valid value), meaning
     * if there was a warning...that warning is no longer relevant.  It may get re-added again
     * via suppression checks below, and that's fine, but if the value is now valid...we can't
     * just keep showing the old error.
     *
     * checkValue() only returns one error at a time right now, so there is only ever one
     * possible warning for a given field at a time.
     *
     */

    this._TrainWarnings.get(trainName).set(field, []);

    /* check if we have an error, and if the error is suppressible or might be just a warning */

    if (train.suppressible(field, errMsg)) {

      /* it's an error, but also suppressible, we can convert to a warning */

      let warning: string = errMsg;

      /* add the warning for this field, of this train, but also ensure we aren't creating duplicates */

      let warnings: string[] = this._TrainWarnings.get(trainName).get(field);

      warnings = warnings.filter((x: string): boolean => x != warning);

      warnings.unshift(warning);

      /* save the updated warnings for this field */

      this._TrainWarnings.get(trainName).set(field, warnings);

      Logger.warning(`[train/table] fieldValid bad value for field [${trainName}] ${field} => ${newValue}: ${warning}`);

    } else {

      /* if there was an error, it can't be suppressed, show it as an error */

      if (errMsg != '') {

        Logger.error(`[train/table] fieldValid bad value for field [${trainName}] ${field} => ${newValue}:  ${errMsg}`);
      }

    }

    /* no error message means it's  ok! */

    return errMsg === '';
  }

  /** fetch the default track for a train passing through this decision point */
  public defaultTrack(train: Train, dp: DecisionPoint): Track {

    const    tracks: Track[]       = dp.outgoingTracks(train._tc._dir);

    return tracks[0];
  }

  public setTrack(trackField: string, dp: DecisionPoint, track: Track, row: IRowNode) {

    const      train: Train         = this.trainOfId(row.data.id)
    const oldIngress: TrackIngress  = row.data[trackField];

    this.focusCell(row, trackField);

    /* generate a field change on the source track ... */

    const   ingress: TrackIngress = new TrackIngress(dp, track);
    const noHistory: boolean      = true;

    this.fieldChanged(trackField, oldIngress, ingress, row, noHistory);

    row.data[trackField] = ingress;

    train.validate();

    this.api.redrawRows({rowNodes: [row]});

    setTimeout(() => {

      let     cols: Column[] = this.api.getAllDisplayedColumns();
      let trackCol: Column   = cols.find((x: Column) => {

        return x.getId() === trackField
      });

      this.api.ensureColumnVisible(trackCol);
      this.api.setFocusedCell(row.rowIndex, trackCol);

    }, 100);
  }

  /* force the value for the 'src' or 'dst' Track fields */
  public setDefaultTrack(trackField: string, dp: DecisionPoint, row: IRowNode) {

    /* select the default track ... */

    const train: Train = this.trainOfId(row.data.id);
    let   track: Track = this.defaultTrack(train, dp);

    /* set it! */

    this.setTrack(trackField, dp, track, row);
  }

  /** force the column value for the given field of the given row (train) */
  public setField(row: IRowNode, field: string, value: any) {

    /* find the train for this row */

    const    train: Train = this.trainOfId(row.data.id);
    const oldValue: any   = row.data[field];

    if (train === null) {

      /* no such train? */

      Logger.error(`[train/table] no train for row: `, row);

      return false;
    }

    if ((field === 'srcdp') || (field === 'dstdp')) {

      /*
       * when switching to a new decision point, we might have to use a fake one (in order to select
       * tracks at the very end of the network.
       *
       * note that 'value' might be a TrackIngress, because the "old" value for a decision point has
       * to be both the decision point and track; when we undo, we have to go back to what the pair
       * was...not just what one or the other was.
       *
       */

      let           dp: DecisionPoint;
      let        track: Track;
      const      train: Train         = this.trainOfId(row.data.id);
      const trackField: string        = (field === 'srcdp') ? 'src' : 'dst';

      if (value instanceof TrackIngress) {

        dp    = (value as TrackIngress).location;
        track = (value as TrackIngress).track;

      } else {

        dp    = value;
        track = this.defaultTrack(train, dp);
      }

      if (!dp._isFake) {

        dp = this.extendDecisionPoint(field, dp, track, train._tc._dir);
      }

      /* updating the src decision point changes both the decision point and the source track */

      row.data[field] = dp;

      this.updateModel(train._id, field, oldValue, dp);

      if (value instanceof TrackIngress) {

        this.setTrack(trackField, dp, track, row);

      } else {

        this.setDefaultTrack(trackField, dp, row);
      }

    } else {

      /* normal atomic cell update */

      row.data[field] = value;

      this.updateModel(train._id, field, oldValue, value);
    }

    train.validate();

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});

    this.focusCell(row, field);
  }

  /** update the given train to set the given field to the given value */
  public updateModel(trainId: number, field: string, oldValue: any, newValue: any): boolean {

    if ((field === 'srcdp') || (field === 'dstdp')) {

      /* trains only save tracks not the decision points */

      return ;
    }

    /* what train is involved? */

    const     train: Train = this.trainOfId(trainId);

    if (train === null) {

      /* no such train? */

      return false;
    }

    /*
     * src/dest are the decision point/track pairs for the origin and destination, the Train
     * only save the track though.
     *
     */

    if ((field === 'src') && (newValue instanceof TrackIngress)) {

      newValue = (newValue as TrackIngress).track;
    }

    if ((field === 'dst') && (newValue instanceof TrackIngress)) {

      newValue = (newValue as TrackIngress).track;
    }

    if ((field === 'departure') && (newValue instanceof Period)) {

      /** the departure column is a Period editor, but Train uses flat seconds as the simulation time point */

      newValue = newValue.asSeconds();
    }

    if ((field === 'trainclass') && (typeof newValue === 'string')) {

      /*
       * this browser currently implements the train classes picker with the agGrid standard drop down...
       * which is string only, so it's the train class name...not a TrainClass instance (which is what Train
       * needs to work with).
       *
       */

      newValue = this.trainClassByName(newValue);
    }

    Logger.debug(`[train/table] updateModel - [${train.quickName()}] ${field}: `, oldValue,` => `, newValue);

    /* update the model */

    const editStatus: boolean = train.edit(field, newValue);

    /* valid the train either way... */

    this.status(`Train: ${train.quickName()} validating...`);

    train.validate();

    if (train.isValid()) {

      /* let user know it's all good */

      Logger.debug(`[train/table] updateModel edit was ok.`);

      this.success(`Train: ${train.quickName()} Ok!`);

    } else {

      /* let the user know what at least the first error is, so they can start fixing... */

      Logger.error(`[train/table] updateModel [${train.quickName()}] has errors: `, train._errors);
    }

    /*
     * if there are any other trains that were already in error, then fall back to showing
     * any errors for them...so that the user knows they have to keep fixing stuff.
     *
     */

    this.refreshErrors();

    if (editStatus === false) {

      /* don't track history of edits that can't work */

      return false;
    }

    /* track the change */

    this.onTrainChanged(train);

    return true;
  }

  /** @brief react to  an edit on one of the fields for a given row (data) */
  public fieldChanged(field: string, oldValue: any, newValue: any, row: IRowNode, noHistory: boolean = false): boolean {

    const data: any = row.data;

    if ((field !== 'srcdp') && (field !== 'dstdp')) {

      /* we don't save the decision points in the Train (only the src/dst tracks). */

      /* update the train involved ... */

      this.updateModel(data.id, field, oldValue, newValue)
    }

    if (noHistory === true) {

      /* we're done! */

      return ;
    }

    /* track the history for this field change */

    const cmd: ICommand = new SetTrainFieldCommand(field, oldValue, newValue, this, row);

    this.historyManager().insert(cmd);

    return true;
  }

  /** @brief helper to decide if this field (of the given data row) is editable or not) */
  public editableField(field: string, data: any) {

    /* what train is involved? */

    const     train: Train = this.trainOfId(data.id);

    if (train === null) {

      /* no such train? */

      return false;
    }

    /* can this train edit this field? */

    return train.canEdit(field);
  }

  /** @brief helper to jump from this table to the plant table for a specific decision point */
  public jumpToDecisionPoint(label: string) {

    Logger.debug('[train/table] jumping to decision point: ' + label);

    /* figure out the decision point id */

    const  dps: DecisionPoint[] = this._scenario.milePoints(true);
    let dpId: number       = 0;

    for (const dp of dps) {

      if (dp._name.toLowerCase() === label.toLowerCase()) {

        dpId = dp._id;
        break;
      }
    }

    if (dpId === 0) {

      Logger.debug('[train/table] no such decision point, ignoring.');

      return;
    }

    Logger.debug('[train/table] emitting view decision point on: ' + dpId);

    this.viewDecisionPoint.emit(dpId);

    return ;
  }

  public jumpToPenalty(label: string) {

    Logger.debug('[train/table] jumping to penalty: ' + label);

    /* figure out the train class id */

    const classes: TrainClass[] = this._scenario.trainClasses();
    let      tcId: number       = 0;

    for (const classItem of classes) {

      if (classItem._name.toLowerCase() === label.toLowerCase()) {

        tcId = classItem._id;
        break;
      }
    }

    if (tcId === 0) {

      Logger.debug('[train/table] no such train class, ignoring.');

      return;
    }

    Logger.debug('[train/table]', 'emitting view train class on: ' + tcId);

    this.viewPenalty.emit(tcId);

    return ;
  }

  public toggleFullScreen() {


    if (this.isFullScreen) {

      Logger.debug('[train/table]', 'exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[train/table] going full screen...');

      const elem = document.getElementById(`ag-grid-train-table_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    const context: any[] = [];
    const gcTrainTable  = params.context.trainTable;

    if (params.value) {

      let dpLabel: string = params.value;

      if (params.value instanceof DecisionPoint) {

        dpLabel = params.value._name;
      }

      if (params.column.getColDef().field === 'srcdp') {

        context.push({
          name: 'Go To: ' + dpLabel,
          action: () => {
            gcTrainTable.jumpToDecisionPoint(dpLabel);
          },
          cssClasses: ['dpsrclaunch']
        });

        context.push({
          name: `Connections...`,
          action: (): void => {

            gcTrainTable.ide.launchWindow(new ConnectionViewerMetadata(
              '',
              '',
              gcTrainTable._scenario,
              dpLabel
            ));
          },
          cssClasses: ['dpsrclaunch']
        });


      } else if (params.column.getColDef().field === 'dstdp') {

        context.push({
          name: 'Go To: ' + dpLabel,
          action: () => {
            gcTrainTable.jumpToDecisionPoint(dpLabel);
          },
          cssClasses: ['dpdstlaunch']
        });

        context.push({
          name: `Connections...`,
          action: (): void => {

            gcTrainTable.ide.launchWindow(new ConnectionViewerMetadata(
              '',
              '',
              gcTrainTable._scenario,
              dpLabel
            ));
          },
          cssClasses: ['dpsrclaunch']
        });

      }
    }

    if (context.length !== 0) {

      context.push('separator');
    }

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (gcTrainTable.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        gcTrainTable.toggleFullScreen();
      }
    });

    context.push('separator');

    /* add in common / generic actions */

    const general1: any[] = [

      'copy',
      'copyWithHeaders',
      'paste',

      'separator',

      'expandAll',
      'contractAll',

      'separator',

    ];

    context.push.apply(context, general1);

    /* add in our custom export functions ... */

    context.push( {
      name: `CSV Export`,
      action: () => {

        gcTrainTable.csvExport();
      }
    });

    context.push( {
      name: `Excel Export`,
      action: () => {

        gcTrainTable.excelExport();
      }
    });

    /* add remaining common / generic actions */

    const general2: any[] = [

      'separator',

      'autoSizeAll'
    ];

    /* pass back the merged menu */

    context.push.apply(context, general2);

    return context;
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[train/table] view init...');

    this.clear();

    const         elem = document.getElementById(`ag-grid-train-table_${this.uuid}`);
    const gcTrainTable = this;

    elem.addEventListener('fullscreenchange', () => {

      gcTrainTable.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[train/table]', '(elem) flipped full screen mode: ', gcTrainTable.isFullScreen);

    }, true);

  }

  /** @brief reset the view to empty */
  public clear() {

    this._scenario = null;
  }

  /** @brief standard destructor */
  public destroy() {

    Logger.debug('[train/table]', 'cleaning up...');

  }

  /** format one of the table column values as a simple string (i.e. for export) */
  public format(value: any, train: Train|undefined): string {

    if (value instanceof TrainClass) {

      return value._name;
    }

    if (value instanceof DecisionPoint) {

      let   name: string = value._name.trim();
      const mile: string = value._mileage.toFixed(2);

      if (name.length >= 16) {

        name = name.substring(0, 16) + '...';
      }

      return `${name} - ${mile}mi`;
    }

    if (value instanceof TrackIngress) {

      let label: string = `${value.track._id} ${Track.typeToString(value.track._type)}${value.track._track}`;

      if (train instanceof Train) {

        /*
         * in browsers/editors where we have access to a train / start / end, we can use the
         * train direction to show the direction of mileage implied by this track.
         *
         */

        /* find the train (so we have direction of travel) */

        const mileage: number[] = [];

        /* find the mileage at either end of the track (one may be null if Junction or end of network) */

        [
          value.track._after,
          value.track._before
        ].filter(x => x !== null).forEach((x: DecisionPoint) => {

          mileage.push(x._mileage);
        });

        /* make the lower mileage first */

        mileage.sort();

        if (train._tc._dir === TrainDirection.EAST) {

          /* show eastbound travel ... */

          label  = '';

          let lhs: number = mileage[0];

          if (mileage.length === 2) {

            lhs = mileage[1];
          }

          label += `(${value.track._id}) `;
          label += `${lhs}`;
          label += ' \u2192 ';

          label += `${Track.typeToString(value.track._type)}${value.track._track}`;

          if (mileage.length === 2) {

            label += ' \u2192 ';
            label += `${mileage[0]}`;
          }

        } else {

          /* show westbound travel ... */

          label  = '';

          label += `(${value.track._id}) `;
          label += `${mileage[0]}`;
          label += ' \u2190 ';

          label += `${Track.typeToString(value.track._type)}${value.track._track}`;

          if (mileage.length === 2) {

            label += ' \u2190 ';
            label += `${mileage[1]}`;
          }

        }

      }

      return label;
    }

    if (value instanceof Period) {

      const d = PeriodRenderer.pad(value._days);
      const h = PeriodRenderer.pad(value._hours);
      const m = PeriodRenderer.pad(value._minutes);
      const s = PeriodRenderer.pad(value._secs);

      return`${d}:${h}:${m}:${s}`;
    }

    /* fell through, we don't know how to format this thing. */

    Logger.error('[train/table] can not format cell', value);

    return '';
  }

  /** export the table to a CSV download */
  public csvExport(): void {

    let tt: TrainTable = this;

    Logger.debug('[train/table] CSV export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(tt._scenario._tag)}.csv`;

    let params: CsvExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (params.node.field == 'type') {

          return params.node.key;
        }

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        let train: Train =  tt.trainOfId(params.node.data.id);

        return tt.format(params.value, train);
      }
    };

    tt.api.exportDataAsCsv(params);
  }

  /** export the table to an Excel download */
  public excelExport(): void {

    let tt: TrainTable = this;

    Logger.debug('[train/table] EXCEL export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(tt._scenario._tag)}.xlsx`;

    let params: ExcelExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (params.node.field == 'type') {

          return params.node.key;
        }

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        let train: Train =  tt.trainOfId(params.node.data.id);

        return tt.format(params.value, train);
      }
    };

    tt.api.exportDataAsExcel(params);
  }

  /** transform (or not) the given DecisionPoint to be a fake one if it needs to be. If not, just return it. */
  public extendDecisionPoint(field: string, dp: DecisionPoint, track: Track, dir: TrainDirection): DecisionPoint {

    /*
     * before we actually set the row, we have to double-check the src/dst tracks; if they
     * in the very outer interlinks (either side of the network), then we can't use the
     * first or last decision point; we have to use one of our fake decision points so that
     * when the user changes values, its consistent, they can switch between the last
     * actual decision point and the fake decision point where required.  See the notes
     * in DecisionPointEditor for details; basically because tracks are selected from one
     * side or the other (but not both at the same time), we have to add fake decision points
     * at the other end of the direction of travel for the train...because otherwise there
     * would be no way for a user to pick the very outermost interlink, because the final
     * decision point on one side other the other would be "internal".
     *
     */

    if (dir !== TrainDirection.WEST) {

      if (dp.next(TrainDirection.WEST) === null) {

        /* if it's the track before any other eastbound track, we have to use the west side fake dp */

        if (track._peers.isWestMost() === true) {

          const fakeDP: DecisionPoint = dp.clone();

          fakeDP._name     = "WEST SIDE";
          fakeDP._eastSide = fakeDP._westSide;
          fakeDP._westSide = null;
          fakeDP._isFake   = true;

          return fakeDP;

        }
      }
    }


    if (dir !== TrainDirection.EAST) {

      if (dp.next(TrainDirection.EAST) === null) {

        /* if it's the track before any other westbound track, we have to use the east side fake dp */

        if (track._peers.isEastMost() === true) {

          const fakeDP: DecisionPoint = dp.clone();

          fakeDP._name     = "EAST SIDE";
          fakeDP._westSide = fakeDP._eastSide;
          fakeDP._eastSide = null;
          fakeDP._isFake   = true;

          return fakeDP;

        }
      }
    }

    /* no faking */

    return dp;
  }

  /** @brief set the table base on this scenario */
  public setScenario(scenario: Scenario = null): boolean {

    Logger.debug('[train/table] refreshing...');

    if (scenario == null) {

      this.clear();

      return true;
    }

    /* save the model */

    this._scenario = scenario;

    /* get the trains report (the grid data) */

    const  report: any[]           = this._scenario.reportTrains();

    /* walk through the rows and build up a row for each, formatted to work with ag-grid */

    this.rowData = [];

    let i = 0;

    for (const row of report) {

      /* make sure the 'type' has a more human-readable name */

      switch (row[1]) {
        case 'act': row[1] = 'Actual';      break;
        case 'evn': row[1] = 'Even';        break;
        case 'exp': row[1] = 'Exponential'; break;
        case 'sch': row[1] = 'Scheduled';   break;
        case 'wky': row[1] = 'Weekly';      break;
      }

      /* format for ag-grid ... */

      const    train: Train         = this.trainOfId( row[0].id);

      const srcLabel: string        = row[6].name;
      let      srcDP: DecisionPoint = this._scenario.milePoints().find(x => x._name === srcLabel);
      const srcTrack: Track         = train._paths[0][0];
      const      src: TrackIngress  = new TrackIngress(srcDP, srcTrack);


      const dstLabel: string = row[7].name;
      let      dstDP: DecisionPoint = this._scenario.milePoints().find(x => x._name === dstLabel);
      const dstTrack: Track         = train._paths[0][1];
      const      dst: TrackIngress  = new TrackIngress(dstDP, dstTrack);

      let dStr = 'P00DT00H00M00S';

      if (typeof row[0].departure === 'object') {

        dStr = ``;

        const pieces: string[] = [];

        pieces.push(parseInt(row[0].departure.day, 10).toString().padStart(2, '0'));
        pieces.push(parseInt(row[0].departure.hour, 10).toString().padStart(2, '0'));
        pieces.push(parseInt(row[0].departure.minute, 10).toString().padStart(2, '0'));
        pieces.push(parseInt(row[0].departure.second, 10).toString().padStart(2, '0'));

        dStr = `P${pieces[0]}DT${pieces[1]}H${pieces[2]}M${pieces[3]}S`;
      }

      const dPer: Period = new Period(dStr);

      srcDP = this.extendDecisionPoint('srcdp', src.location, src.track, train._tc._dir);
      dstDP = this.extendDecisionPoint('dstdp', dst.location, dst.track, train._tc._dir);

      /* set the row! */

      this.rowData.push({
             row: i,
            type: row[1],
              id: row[0].id,
            name: row[0].name,
      trainclass: this.trainClassByName(row[2].name),
          length: parseInt(row[3], 10),
         tonnage: parseInt(row[4], 10),
        priority: parseInt(row[5], 10),
            tcid: this.trainClassByName(row[2].name)._id,
           srcdp: srcDP,
             src,
           dstdp: dstDP,
             dst,
       departure: dPer,
            days: row[0].dayofweek,
           daily: row[0].daily,
          weekly: row[0].weekly,
        overtake: row[0].maxdelay,
            seed: row[0].seed,
    percentearly: row[0].percentearly,
           early: row[0].early,
            late: row[0].late,
    distribution: row[0].distribution,
        warnings: ''
      });

      i++;
    }

    /* set the column definitions */

    this.api.setGridOption('columnDefs', this.columnDefs);

    /* set the raw data for the entire grid */

    this.api.setGridOption('rowData', this.rowData);

    /* this.gridColumnApi.autoSizeAllColumns(); */

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */

    /* all done */

    return true;
  }


  @HostListener('window:resize')
  public resize() {

    Logger.debug('[train/table]', 'resizing...');

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */
  }

}
